import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Edit2, Printer, XCircle } from "lucide-react";
import Swal from "sweetalert2";
import { obtenerListaOC, cancelarOrdenCompra } from "../services/ordenesCompraService";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import TableToolbar from "../components/TableToolbar";
import Toast, { useToast } from "../components/Toast";

const fmt = (n) =>
  `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const displayDate = (d) => {
  if (!d) return "";
  const parts = d.includes("/") ? d.split("/") : d.split("-");
  if (parts.length !== 3) return d;
  const [y, m, day] = parts;
  return `${day}/${m}/${y}`;
};

const StatusBadge = ({ status }) => {
  const cancelada = status === "C";
  return (
    <span style={{
      fontSize: "var(--fs-xs)", padding: "2px 10px",
      borderRadius: "var(--radius-full, 9999px)",
      fontWeight: "var(--fw-medium)",
      background: cancelada ? "var(--clr-warning-bg, #FFF3CD)" : "var(--clr-success-bg, #DCFCE7)",
      color:      cancelada ? "var(--clr-warning,    #92400E)" : "var(--clr-success,    #065F46)",
    }}>
      {cancelada ? "Cancelada" : "Activa"}
    </span>
  );
};

export default function OrdenesCompraListaPage() {
  const navigate = useNavigate();
  const empresaId = Number(localStorage.getItem("empresaId") || 1);
  const { toasts, closeToast, error: toastError, success: toastSuccess } = useToast();

  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState("");
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 20;

  const cargar = useCallback(async (pg, q) => {
    setLoading(true);
    try {
      const data = await obtenerListaOC({ empresa: empresaId, page: pg, pageSize: PAGE_SIZE, search: q });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toastError("Error al cargar órdenes de compra.");
    } finally {
      setLoading(false);
    }
  }, [empresaId]); // eslint-disable-line

  useEffect(() => { cargar(page, search); }, [page, search, cargar]);

  const handleSearch = (q) => { setSearch(q); setPage(1); };

  const handleCancelar = async (row) => {
    const res = await Swal.fire({
      title: `¿Cancelar OC-${row.ordenCompra}?`,
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No",
      confirmButtonColor: "#dc2626",
    });
    if (!res.isConfirmed) return;
    try {
      await cancelarOrdenCompra(row.ordenCompra, empresaId);
      toastSuccess(`OC-${row.ordenCompra} cancelada.`);
      cargar(page, search);
    } catch (e) {
      toastError(e?.response?.data?.message || "Error al cancelar.");
    }
  };

  const columns = [
    { key: "ordenCompra", title: "N° OC", style: { width: 80 } },
    { key: "fecha",       title: "Fecha",    style: { width: 110 }, render: (r) => displayDate(r.fecha) },
    { key: "proveedor",   title: "Clave",    style: { width: 90 } },
    { key: "nombreProv",  title: "Proveedor" },
    {
      key: "moneda", title: "Mon.", style: { width: 60 },
      render: (r) => (
        <span style={{
          fontSize: "var(--fs-xs)", padding: "2px 8px",
          borderRadius: "var(--radius-full)", fontWeight: "var(--fw-medium)",
          background: r.moneda === "D" ? "#DBEAFE" : "#DCFCE7",
          color:      r.moneda === "D" ? "#1E40AF" : "#065F46",
        }}>
          {r.moneda === "D" ? "USD" : "MXN"}
        </span>
      ),
    },
    { key: "total",   title: "Total",   style: { width: 120 }, className: "num", render: (r) => fmt(r.total) },
    {
      key: "surtida", title: "Surtida", style: { width: 80 },
      render: (r) => r.surtida ? "✔" : "—",
    },
    { key: "status",  title: "Estatus", style: { width: 100 }, render: (r) => <StatusBadge status={r.status} /> },
    {
      key: "_actions", title: "", style: { width: 90 },
      render: (r) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
          <button
            className="lmd-btn lmd-btn-ghost lmd-btn-sm"
            title="Imprimir"
            onClick={() => navigate(`/proveedores/ordenes-compra/${r.ordenCompra}/print`)}
          >
            <Printer size={14} />
          </button>
          <button
            className="lmd-btn lmd-btn-ghost lmd-btn-sm"
            title="Editar"
            onClick={() => navigate(`/proveedores/ordenes-compra/${r.ordenCompra}/editar`)}
          >
            <Edit2 size={14} />
          </button>
          {r.status !== "C" && (
            <button
              className="lmd-btn lmd-btn-ghost lmd-btn-sm"
              title="Cancelar"
              style={{ color: "var(--clr-danger, #dc2626)" }}
              onClick={() => handleCancelar(r)}
            >
              <XCircle size={14} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader title="Órdenes de Compra" subtitle="Gestión de órdenes a proveedores" />

      <div className="lmd-card">
        <TableToolbar
          searchValue={search}
          onSearchChange={handleSearch}
          onSearch={() => {}}
          searchPlaceholder="Buscar por N°, proveedor..."
          addText="Nueva OC"
          onAdd={() => navigate("/proveedores/ordenes-compra/nueva")}
        />

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--clr-text-muted)" }}>
            Cargando…
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            keyField="ordenCompra"
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        )}
      </div>
    </>
  );
}
